// src/components/navbar/mobileNav/index.js
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { IoSearchSharp } from "react-icons/io5";
import { FaRegHeart } from "react-icons/fa";
import { HiOutlineMenuAlt4 } from "react-icons/hi";
import { IoCloseOutline } from "react-icons/io5";
import { mobileMenuData } from "./mobileMenuData";
import { useWishlist } from "context/WishlistContext";
import { useBlog } from "context/BlogContext";
import { useCustomer } from "context/CustomerContext";
import { getMyDistributorStatus } from "services/medusa/distributor";
import StoreLocator from "../../storelocator/StoreLocator";
import SearchModal from '../../search/SearchModal';
import { useProducts } from 'hooks/useProducts';
import { lockScroll, unlockScroll } from "utils/scrollLock";
import "./MobileNavbar.css";

// Iconos SVG para navegación interna
const ChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const MinusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ============================================
// COMPONENTE: Product Card Mini
// ============================================
const ProductCardMini = ({ product }) => (
  <a href={product.url} className="mobile-product-card">
    <div className="mobile-product-image">
      <img src={product.image} alt={product.name} />
    </div>
    <span className="mobile-product-name">{product.name}</span>
  </a>
);

// ============================================
// COMPONENTE: Products Carousel
// ============================================
const ProductsCarousel = ({ title, subtitle, products }) => (
  <div className="mobile-products-section">
    <h4 className="mobile-products-title">{title}</h4>
    {subtitle && <p className="mobile-products-subtitle">{subtitle}</p>}
    <div className="mobile-products-carousel">
      {products.map((product) => (
        <ProductCardMini key={product.id} product={product} />
      ))}
    </div>
  </div>
);

// ============================================
// COMPONENTE: Accordion Item
// ============================================
const AccordionItem = ({ accordion, isOpen, onToggle }) => (
  <div className="mobile-accordion">
    <button 
      className="mobile-accordion-header"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span>{accordion.title}</span>
      {isOpen ? <MinusIcon /> : <PlusIcon />}
    </button>
    <div className={`mobile-accordion-content ${isOpen ? 'is-open' : ''}`}>
      {accordion.links.map((link, idx) => (
        link.url ? (
          <a key={idx} href={link.url} className="mobile-accordion-link">
            {link.label}
          </a>
        ) : (
          <span key={idx} className="mobile-accordion-link is-label">
            {link.label}
          </span>
        )
      ))}
    </div>
  </div>
);

// ============================================
// COMPONENTE: Blog Post Card
// ============================================
const BlogPostCard = ({ post }) => (
  <a href={post.url} className="mobile-blog-card">
    <div className="mobile-blog-image">
      <img src={post.image} alt={post.title} />
    </div>
    <div className="mobile-blog-content">
      <span className="mobile-blog-category">{post.category}</span>
      <h5 className="mobile-blog-title">{post.title}</h5>
      <span className="mobile-blog-date">{post.date}</span>
    </div>
  </a>
);

// ============================================
// COMPONENTE: Promo Banner
// ============================================
const PromoBanner = ({ promo }) => (
  <a href={promo.url} className="mobile-promo-banner">
    <div className="mobile-promo-text">
      <span className="mobile-promo-title">{promo.title}</span>
      <span className="mobile-promo-subtitle">{promo.subtitle}</span>
    </div>
    <div className="mobile-promo-image">
      <img src={promo.image} alt={promo.title} />
    </div>
  </a>
);

// ============================================
// COMPONENTE: Blog Posts Carousel (bottom of main menu)
// ============================================
const BlogPostsCarousel = ({ posts }) => {
  if (!posts || posts.length === 0) return null;

  // Calculate reading time inline
  const getReadTime = (post) => {
    let wordCount = 0;
    if (post.blocks) {
      post.blocks.forEach(block => {
        if (block.type === 'text' && block.data) {
          if (block.data.heading) wordCount += block.data.heading.split(/\s+/).length;
          if (block.data.body) wordCount += block.data.body.split(/\s+/).length;
        }
        if (block.type === 'pullquote' && block.data?.quote) {
          wordCount += block.data.quote.split(/\s+/).length;
        }
      });
    }
    const minutes = Math.max(1, Math.ceil(wordCount / 200));
    return `${minutes} min`;
  };

  return (
    <div className="mobile-brand-carousel">
      {posts.map((post) => (
        <a key={post.id} href={`/journal/${post.slug}`} className="mobile-brand-card" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="mobile-brand-image">
            <img src={post.thumbnail} alt={post.title} />
          </div>
          <span className="mobile-brand-title">• {post.category || 'Journal'}</span>
          <span className="mobile-brand-post-title">{post.title}</span>
          <span className="mobile-brand-readtime">Tiempo de lectura: {getReadTime(post)}</span>
        </a>
      ))}
    </div>
  );
};

// ============================================
// COMPONENTE: Submenu Panel
// ============================================
// Los links NO cierran el menú: navegan con el menú aún encima, así no se ve
// la página de fondo antes de que cargue la nueva.
const SubmenuPanel = ({ section, onBack, onAction, realBlogPosts }) => {
  const [openAccordions, setOpenAccordions] = useState({});
  const submenu = section.submenu;

  const toggleAccordion = (id) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="mobile-submenu-panel">
      {/* Header con botón back */}
      <div className="mobile-submenu-header">
        <button className="mobile-back-button" onClick={onBack}>
          <ArrowLeftIcon />
        </button>
        <span className="mobile-submenu-title">{submenu.title}</span>
      </div>

      {/* Contenido scrolleable */}
      <div className="mobile-submenu-content">
        {/* Links directos */}
        {submenu.links && (
          <div className="mobile-links-section">
            {submenu.links.map((link, idx) => (
              link.action ? (
                <button
                  key={idx}
                  type="button"
                  className="mobile-menu-link"
                  onClick={() => onAction(link.action)}
                >
                  {link.label}
                </button>
              ) : (
                <a key={idx} href={link.url} className="mobile-menu-link">
                  {link.label}
                </a>
              )
            ))}
          </div>
        )}

        {/* Accordions */}
        {submenu.accordions && (
          <div className="mobile-accordions-section">
            {submenu.accordions.map((accordion) => (
              <AccordionItem
                key={accordion.id}
                accordion={accordion}
                isOpen={openAccordions[accordion.id]}
                onToggle={() => toggleAccordion(accordion.id)}
              />
            ))}
          </div>
        )}

        {/* Blog Posts (para The Journalist) */}
        {submenu.blogPosts && (
          <div className="mobile-blog-section">
            {submenu.description && (
              <p className="mobile-blog-description">{submenu.description}</p>
            )}
            {submenu.categories && (
              <div className="mobile-blog-categories">
                {submenu.categories.map((cat, idx) => (
                  <a key={idx} href={cat.url} className="mobile-category-link">
                    {cat.label}
                  </a>
                ))}
              </div>
            )}
            <div className="mobile-blog-posts">
              {(realBlogPosts && realBlogPosts.length > 0
                ? realBlogPosts.map((p) => ({
                    id: p.id,
                    url: `/journal/${p.slug}`,
                    image: p.thumbnail,
                    title: p.title,
                    category: p.category || 'Journal',
                    date: (p.publishedAt || p.createdAt)
                      ? new Date(p.publishedAt || p.createdAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '',
                  }))
                : submenu.blogPosts
              ).map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        )}

        {/* Featured Products Section */}
        {submenu.featuredSection && (
          <ProductsCarousel
            title={submenu.featuredSection.title}
            subtitle={submenu.featuredSection.subtitle}
            products={submenu.featuredSection.products}
          />
        )}

        {/* Secondary Products Section */}
        {submenu.secondarySection && (
          <ProductsCarousel
            title={submenu.secondarySection.title}
            products={submenu.secondarySection.products}
          />
        )}

        {/* Promo Banner */}
        {submenu.promo && <PromoBanner promo={submenu.promo} />}
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE: Main Menu Panel
// ✅ CORREGIDO: Reemplaza el primer link con cuenta dinámica
// ============================================
const MainMenuPanel = ({ onSectionClick, onAction, onClose, isLoggedIn, userName, onLoginClick, blogPosts }) => {

  // Handler para click en cuenta: con sesión navega (con el menú encima);
  // sin sesión cierra el menú y abre el modal de login.
  const handleAccountClick = () => {
    if (isLoggedIn) {
      window.location.href = '/account';
    } else {
      onClose();
      if (onLoginClick) onLoginClick();
    }
  };

  return (
    <div className="mobile-main-panel">
      {/* Links principales */}
      <div className="mobile-main-links">
        {mobileMenuData.mainSections.map((section) => (
          section.hasSubmenu ? (
            <button
              key={section.id}
              className="mobile-main-link"
              onClick={() => onSectionClick(section)}
            >
              <span>{section.label}</span>
              <ChevronRightIcon />
            </button>
          ) : (
            <a
              key={section.id}
              href={section.url}
              className="mobile-main-link"
              onClick={section.action ? (e) => { e.preventDefault(); onAction(section.action, section); } : undefined}
            >
              <span>{section.label}</span>
            </a>
          )
        ))}
      </div>

      {/* Links secundarios - Grid 2x2 */}
      <div className="mobile-secondary-links">
        {/* ✅ Link de cuenta dinámico - PRIMERO */}
        <button 
          className="mobile-secondary-link mobile-account-link"
          onClick={handleAccountClick}
        >
          {isLoggedIn ? `Bienvenido, ${userName}` : 'Ingresar'}
        </button>
        
        {mobileMenuData.secondaryLinks.map((link, idx) => (
          <a key={idx} href={link.url} className="mobile-secondary-link">
            {link.label}
          </a>
        ))}
      </div>

      {/* Blog Posts Carousel */}
      <BlogPostsCarousel posts={blogPosts} />

      {/* Footer del menú */}
      <div className="mobile-menu-footer">
        <p>Ácidos Activos 15% de concentración</p>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL: MobileNavbar
// ============================================
const MobileNavbar = ({ 
  onCartClick, 
  cartCount = 0, 
  onLoginClick, 
  isLoggedIn = false, 
  userName = '' 
}) => {
  const { wishlistCount } = useWishlist();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isOverLightBg, setIsOverLightBg] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [navReady, setNavReady] = useState(false);
  const savedScrollY = useRef(0);
  const [portalContainer, setPortalContainer] = useState(null);
  const { products: medusaProducts } = useProducts();

  // Socios: si hay sesión de cliente que no es socia, el aviso se muestra
  // encima del menú (sin navegar). El estado se consulta al picar.
  const { isAuthenticated, token } = useCustomer();
  const [showNotPartner, setShowNotPartner] = useState(false);
  const [isStoreLocatorOpen, setIsStoreLocatorOpen] = useState(false);

  // "Localiza un distribuidor" (footer) → mapa a pantalla completa.
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth >= 746) return;
      setIsStoreLocatorOpen(true);
    };
    window.addEventListener('phys:open-store-locator', handler);
    return () => window.removeEventListener('phys:open-store-locator', handler);
  }, []);

  const handleMenuAction = async (action, section) => {
    if (action === 'store-locator') {
      setIsStoreLocatorOpen(true);
      return;
    }
    if (action === 'socios') {
      if (!isAuthenticated || !token) {
        window.location.href = section.url;
        return;
      }
      try {
        const status = await getMyDistributorStatus(token);
        if (status?.is_distributor) {
          window.location.href = section.url;
        } else {
          setShowNotPartner(true);
        }
      } catch {
        // Si falla la consulta, dejamos que /socio resuelva.
        window.location.href = section.url;
      }
    }
  };

  // Blog posts publicados (backend Medusa vía BlogContext)
  const { posts: allBlogPosts } = useBlog();
  const blogPosts = React.useMemo(
    () => (allBlogPosts || []).filter(p => p.status === 'published').slice(0, 4),
    [allBlogPosts]
  );

  // Si el usuario vuelve con "atrás" y el navegador restaura la página desde
  // caché (bfcache), el menú seguiría abierto porque los links navegan con el
  // menú encima. Lo cerramos sin animación.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setIsOpen(false);
        setActiveSection(null);
        setShowSubmenu(false);
        setSlideDirection('');
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape' || isStoreLocatorOpen) return;
      if (showNotPartner) setShowNotPartner(false);
      else closeMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showNotPartner, isStoreLocatorOpen]);

  // Setup portal container
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalContainer(document.body);
    }
  }, []);

  // Habilitar transiciones del navbar solo tras el primer paint (2 frames), para
  // que su posición inicial no anime y no se vea el "brinco" al entrar.
  useEffect(() => {
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setNavReady(true)));
    return () => cancelAnimationFrame(id);
  }, []);

  // Cerrar el menú mobile al pasar a desktop. El panel está portaleado a <body>,
  // así que si el usuario abre el menú en móvil y agranda a escritorio (>=746px,
  // donde .phys-mobile-nav pasa a display:none), el panel se quedaba visible.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 746) {
        setIsOpen(false);
        setActiveSection(null);
        setShowSubmenu(false);
        setSlideDirection('');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

 // Detectar scroll para cambiar background, posición y scroll-reverse
useEffect(() => {
  let lastScrollY = 0;

  const handleScroll = () => {
    const scrollY = window.scrollY;
    const bannersHeight = 120; // Altura de los banners

    const navbar = document.querySelector('.mobile-navbar-transparent');
    if (!navbar) return;

    // Fase 1: Mientras se scrollean los banners, deslizar navbar hacia arriba
    if (scrollY <= bannersHeight) {
      const navbarTop = bannersHeight - scrollY;
      navbar.style.top = `${navbarTop}px`;
      navbar.style.transform = 'translateY(0)';
    } else {
      // Fase 2: Después de los banners, scroll-reverse (ocultar al bajar, mostrar al subir)
      navbar.style.top = '0px';

      if (scrollY > lastScrollY && scrollY > bannersHeight + 100) {
        // Scrolling down — ocultar navbar
        navbar.style.transform = 'translateY(-100%)';
      } else {
        // Scrolling up o near top — mostrar navbar
        navbar.style.transform = 'translateY(0)';
      }
    }

    // Detectar si el navbar está sobre fondo claro
    if (scrollY <= bannersHeight) {
      const navbarTop = bannersHeight - scrollY;
      const navbarMid = navbarTop + 30; // centro del navbar
      const navbarBottom = navbarTop + 60;
      const sections = document.querySelectorAll('[data-header-theme]');
      let overLight = false;

      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        // Overlap check con margen de 5px para bordes exactos
        if (rect.top <= navbarBottom + 5 && rect.bottom >= navbarTop) {
          overLight = section.dataset.headerTheme === 'light';
          break;
        }
      }

      // Fallback: usar elementFromPoint para detectar el fondo detrás del navbar
      if (!overLight && navbarMid > 0) {
        const el = document.elementFromPoint(window.innerWidth / 2, navbarMid);
        if (el) {
          const themed = el.closest('[data-header-theme]');
          if (themed) {
            overLight = themed.dataset.headerTheme === 'light';
          }
        }
      }

      setIsOverLightBg(overLight);
    } else {
      setIsOverLightBg(false);
    }

    // Cambiar background cuando ya scrolleó más allá de los banners
    setHasScrolled(scrollY > bannersHeight);
    lastScrollY = scrollY;
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Ejecutar al montar

  return () => window.removeEventListener('scroll', handleScroll);
}, []);

  // Scroll lock — usa utils/scrollLock.js + clase CSS mega-scroll-locked (con !important)
  useEffect(() => {
    if (isOpen) {
      savedScrollY.current = window.scrollY;
      lockScroll();
      document.body.classList.add('mega-scroll-locked');
      document.documentElement.classList.add('mega-scroll-locked');
    } else {
      unlockScroll();
      document.body.classList.remove('mega-scroll-locked');
      document.documentElement.classList.remove('mega-scroll-locked');
    }
    return () => {
      if (isOpen) {
        unlockScroll();
        document.body.classList.remove('mega-scroll-locked');
        document.documentElement.classList.remove('mega-scroll-locked');
      }
    };
  }, [isOpen]);

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setActiveSection(null);
    setShowSubmenu(false);
    setSlideDirection('');
    setIsOpen(true);
  };

  // El menú se desvanece (0.6s, ver .mobile-menu-overlay); el submenu se
  // resetea al terminar para que no salte mientras sale.
  const closeTimer = useRef(null);
  const closeMenu = () => {
    setIsOpen(false);
    setShowNotPartner(false);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setActiveSection(null);
      setShowSubmenu(false);
      setSlideDirection('');
    }, 600);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const goToSection = (section) => {
    setActiveSection(section);
    setSlideDirection('slide-left');
    // Pequeño delay para que el DOM renderice el submenu antes del slide
    requestAnimationFrame(() => {
      setShowSubmenu(true);
    });
  };

  const goBack = () => {
    setSlideDirection('slide-right');
    setShowSubmenu(false);
    // Esperar a que termine la animación antes de desmontar el submenu
    setTimeout(() => {
      setActiveSection(null);
      setSlideDirection('');
    }, 450);
  };

  // Handler para abrir el cart
  const handleCartClick = () => {
    if (isOpen) closeMenu();
    if (onCartClick) onCartClick();
  };

  // Handler para abrir búsqueda
  const handleSearchClick = () => {
    if (isOpen) closeMenu();
    setIsSearchOpen(true);
  };

  // Navbar - SIN icono de usuario.
  // Ojo: es JSX en línea (no un componente definido aquí adentro) para que
  // React no lo desmonte y vuelva a montar en cada render.
  const defaultNavbar = (
    <header className={`mobile-navbar-transparent ${navReady ? '' : 'is-initializing'} ${hasScrolled ? 'has-scrolled' : ''} ${isOverLightBg && !hasScrolled ? 'is-over-light' : ''}`}>
      <div className="mobile-navbar-inner">
        {/* Logo */}
        <a href="/" className="mobile-logo">
          <img src="/logo-phys.svg" alt="Physiology" />
        </a>

        {/* Actions - SIN icono de usuario */}
        <div className="mobile-navbar-actions">
          <button className="mobile-action-btn" aria-label="Buscar" onClick={handleSearchClick}>
            <IoSearchSharp size={22} />
          </button>

          <a href="/wishlist" className="mobile-action-btn" aria-label="Favoritos" style={{ position: 'relative' }}>
            <FaRegHeart size={20} />
            {wishlistCount > 0 && (
              <span className="mobile-cart-count">{wishlistCount}</span>
            )}
          </a>
          
          {/* Cart button */}
          <button 
            className="mobile-action-btn mobile-cart-btn"
            onClick={handleCartClick}
          >
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="mobile-cart-count">{cartCount}</span>
            )}
          </button>
          
          <button 
            className="mobile-action-btn mobile-menu-btn" 
            onClick={openMenu}
            aria-label="Abrir menú"
          >
            <HiOutlineMenuAlt4 size={24} />
          </button>
        </div>
      </div>
    </header>
  );

  // Modal del menú expandido. Siempre montado (oculto con CSS) para que la
  // entrada y la salida puedan animarse con el slide.
  const menuModal = portalContainer && createPortal(
      <div
        className={`mobile-menu-overlay ${isOpen ? 'is-open' : ''}`}
        aria-hidden={!isOpen}
      >
        {/* Header del menú */}
        <header className="mobile-menu-header">
          <a href="/" className="mobile-logo" onClick={closeMenu}>
            <img src="/logo-phys.svg" alt="Physiology" />
          </a>
          <div className="mobile-navbar-actions">
            <button className="mobile-action-btn" aria-label="Buscar" onClick={handleSearchClick}>
              <IoSearchSharp size={22} />
            </button>
            
            <a href="/wishlist" className="mobile-action-btn" aria-label="Favoritos" style={{ position: 'relative' }}>
              <FaRegHeart size={20} />
              {wishlistCount > 0 && (
                <span className="mobile-cart-count">{wishlistCount}</span>
              )}
            </a>
            
            {/* Cart en menú abierto */}
            <button 
              className="mobile-action-btn mobile-cart-btn"
              onClick={handleCartClick}
            >
              <span>Cart</span>
              {cartCount > 0 && (
                <span className="mobile-cart-count">{cartCount}</span>
              )}
            </button>
            
            <button 
              className="mobile-action-btn mobile-close-btn" 
              onClick={closeMenu}
              aria-label="Cerrar menú"
            >
              <IoCloseOutline size={28} />
            </button>
          </div>
        </header>

        {/* Contenido del menú con transiciones slide */}
        <div className="mobile-menu-body">
          <div className={`mobile-panels-wrapper ${showSubmenu ? 'show-submenu' : ''} ${slideDirection}`}>
            {/* Panel principal */}
            <div className="mobile-panel mobile-panel-main">
              <MainMenuPanel
                onSectionClick={goToSection}
                onAction={handleMenuAction}
                onClose={closeMenu}
                isLoggedIn={isLoggedIn}
                userName={userName}
                onLoginClick={onLoginClick}
                blogPosts={blogPosts}
              />
            </div>
            
            {/* Panel de submenu */}
            <div className="mobile-panel mobile-panel-sub">
              {activeSection && (
                <SubmenuPanel
                  section={activeSection}
                  onBack={goBack}
                  onAction={handleMenuAction}
                  realBlogPosts={blogPosts}
                />
              )}
            </div>
          </div>
        </div>

        {/* Aviso "Acceso exclusivo para socios" encima del menú */}
        {showNotPartner && (
          <div
            className="partner-modal-overlay mobile-partner-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-partner-modal-title"
            onClick={(e) => { if (e.target === e.currentTarget) setShowNotPartner(false); }}
          >
            <div className="partner-modal mobile-partner-modal">
              <button
                type="button"
                className="mobile-partner-modal__close"
                aria-label="Cerrar"
                onClick={() => setShowNotPartner(false)}
              >
                <IoCloseOutline size={26} />
              </button>
              <h2 id="mobile-partner-modal-title" className="partner-modal__title">Acceso exclusivo para socios</h2>
              <p className="partner-modal__text">
                Tu cuenta es de cliente. El <strong>panel de socio</strong> es exclusivo
                para socios y distribuidores Physiology.
              </p>
              <div className="partner-modal__actions">
                <a className="partner-shell__login-btn" href="/account">
                  Ir a mi cuenta
                </a>
                <a className="partner-modal__link" href="/socios-y-distribuidores">
                  ¿Cómo ser socio Physiology?
                </a>
              </div>
            </div>
          </div>
        )}
      </div>,
      portalContainer
  );

  return (
    <>
      {defaultNavbar}
      {menuModal}
      {/* Mapa de afiliados: pantalla completa encima del menú; al cerrarlo
          se regresa al menú. */}
      <StoreLocator
        isOpen={isStoreLocatorOpen}
        onClose={() => setIsStoreLocatorOpen(false)}
      />
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        products={medusaProducts}
      />
    </>
  );
};

export default MobileNavbar;
